import LoginForm from '../../components/forms/login/login.form';
import { AuthDto } from '../../services/dtos/auth.dto';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/use-auth.hook';
import { Dialog, DialogContent, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import logoGeneralAssembly from '../../assets/img/assemblee.png';
import logoOfficeTradeUnion from '../../assets/img/bsca.png';
import logoCOPE from '../../assets/img/cope.png';
import logoTerritory from '../../assets/img/territoire.png';
import './home.page.scss';
import { getCurrentAccountUser } from '../../services/user.service';
import {
    getMeetingFileContentForCurrentUser,
    getMeetingFilesForCurrentUser,
    getUpcomingMeetingsForCurrentUser,
    updateMeetingAttendance,
} from '../../services/general-meeting.service';
import { showAxiosMessageError } from '../../utils/error-message.util';
import { useSnackbars } from '../../hooks/snackbar.hook';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from '../../hooks/use-local-storage.hook';
import { MandateEstablishmentDto } from '../../services/dtos/mandate-establishment-dto';
import { MandateGeneralDto } from '../../services/dtos/mandate-general.dto';
import { MandateTerritoryDto } from '../../services/dtos/mandate-territory.dto';
import { MeetingDto, MyProxyGivenToDto } from '../../services/dtos/meeting.dto';
import { UserAccountDto } from '../../services/dtos/user-account.dto';
import MeetingAttendance from '../../components/action/meeting-attendance';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import MeetingFilesUi from '../../components/userInterfaces/meeting-files/meeting-files.ui';
import { FileDto } from '../../services/dtos/file.dto';

const isEauPotableSkillCenter = (skillCenter: string): boolean =>
    skillCenter
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .includes('eau potable');

interface HomeUpcomingMeetingItem {
    id: string;
    date: string;
    startTime: string;
    location: string;
    pointsReunion?: string;
    title: string;
    sortDate: number;
    status: string;
    attendance?: boolean | null;
    hasDocument: boolean;
    mandateId?: string | null;
    establishmentId?: string | null;
    territoryId?: string | null;
    suiviPresence?: boolean;
    delegationPouvoirsActivee?: boolean;
    myProxyGivenTo?: MyProxyGivenToDto | null;
}

const parseMeetingDateTime = (date: string, startTime: string): number => {
    const normalizedTime = startTime ? (startTime.includes(':') ? startTime : startTime.replace('h', ':')) : '00:00';

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
        const [day, month, year] = date.split('/').map(Number);
        const [hours, minutes] = normalizedTime.split(':').map(Number);

        return new Date(year, month - 1, day, hours, minutes).getTime();
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return new Date(`${date}T${normalizedTime}`).getTime();
    }

    return new Date(`${date} ${normalizedTime}`).getTime();
};

/**
 * Page d'accueil de l'application
 * @constructor
 */
export default function HomePage() {
    //Récupération du contexte
    const auth = useAuth();
    //Disable ou non le bouton de la validation
    const [loading, setLoading] = useState(false);
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //Navigate
    const navigate = useNavigate();
    // Données de l'utilisateur nécessaires pour la page en cours
    const [user, setUser] = useLocalStorage<UserAccountDto | null>('userAccount', null);
    const [upcomingMeetings, setUpcomingMeetings] = useState<HomeUpcomingMeetingItem[]>([]);
    const [loadingUpcomingMeetings, setLoadingUpcomingMeetings] = useState(false);
    const [meetingAttendanceLoadingId, setMeetingAttendanceLoadingId] = useState<string>();
    const [homeMeetingFiles, setHomeMeetingFiles] = useState<FileDto[]>([]);
    const [homeMeetingFilesLoading, setHomeMeetingFilesLoading] = useState(false);
    const [selectedMeeting, setSelectedMeeting] = useState<HomeUpcomingMeetingItem>();
    const mandatesEstablishment = useMemo(
        () =>
            (user?.mandatesEstablishment ?? []).filter(
                (value, index, mandates) =>
                    index ===
                    mandates.findIndex(
                        mandate =>
                            mandate.establishmentId === value.establishmentId &&
                            mandate.establishmentLabel === value.establishmentLabel &&
                            mandate.skillCenter === value.skillCenter
                    )
            ),
        [user?.mandatesEstablishment]
    );
    const mandatesTerritory = useMemo(() => user?.mandatesTerritory ?? [], [user?.mandatesTerritory]);
    const mandatesBasin = useMemo(() => user?.mandatesBasin ?? [], [user?.mandatesBasin]);
    const hasAuthenticatedUser = Boolean(auth.user);
    const authUserId = auth.user?.id;
    const authLoggedInAs = auth.user?.emailLoggedInAS;

    // Comportement au submit du formulaire Login
    const handlerSubmit = async (e: AuthDto) => {
        setLoading(true);
        auth.signin(e);
        setLoading(false);
    };

    useEffect(() => {
        if (hasAuthenticatedUser) {
            //Récupération de l'utilisateur courant
            getCurrentAccountUser()
                .then(result => {
                    setUser(result);
                })
                .catch(error => {
                    console.error("Erreur lors de la récupération de l'utilisateur");
                    showAxiosMessageError(error, showMessage);
                });
        }
    }, [authLoggedInAs, authUserId, hasAuthenticatedUser, setUser, showMessage]);

    const userId = user?.extId;

    useEffect(() => {
        if (!hasAuthenticatedUser || !userId) {
            setUpcomingMeetings([]);
            return;
        }

        let isMounted = true;
        setLoadingUpcomingMeetings(true);

        getUpcomingMeetingsForCurrentUser(5)
            .then(result => {
                if (!isMounted) {
                    return;
                }

                const nextMeetings = (result.items ?? [])
                    .map((meeting: MeetingDto) => ({
                        id: meeting.id,
                        date: meeting.date,
                        startTime: meeting.startTime,
                        location: meeting.location,
                        pointsReunion: meeting.pointsReunion,
                        title: meeting.title ?? 'Réunion',
                        sortDate: parseMeetingDateTime(meeting.date, meeting.startTime),
                        status: meeting.status,
                        attendance: meeting.attendance,
                        hasDocument: meeting.hasDocument,
                        mandateId: meeting.mandateId,
                        establishmentId: meeting.establishmentId,
                        territoryId: meeting.territoryId,
                        suiviPresence: meeting.suiviPresence,
                        delegationPouvoirsActivee: meeting.delegationPouvoirsActivee,
                        myProxyGivenTo: meeting.myProxyGivenTo,
                    }))
                    .filter(meeting => !Number.isNaN(meeting.sortDate))
                    .sort((left, right) => left.sortDate - right.sortDate)
                    .slice(0, 5);

                setUpcomingMeetings(nextMeetings);
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }

                console.error('Erreur lors de la récupération des prochaines réunions');
                showAxiosMessageError(error, showMessage);
                setUpcomingMeetings([]);
            })
            .finally(() => {
                if (isMounted) {
                    setLoadingUpcomingMeetings(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [hasAuthenticatedUser, showMessage, userId]);

    const handlerUpdateMeetingAttendance = async (meetingId: string, attendance: boolean) => {
        setMeetingAttendanceLoadingId(meetingId);

        try {
            const updatedAttendance = await updateMeetingAttendance(meetingId, attendance);
            setUpcomingMeetings(currentMeetings =>
                currentMeetings.map(meeting =>
                    meeting.id === meetingId ? { ...meeting, attendance: updatedAttendance } : meeting
                )
            );
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la réponse de présence');
            showAxiosMessageError(error as Error, showMessage);
        } finally {
            setMeetingAttendanceLoadingId(undefined);
        }
    };

    const handlerProxyChange = (meetingId: string, myProxyGivenTo: MyProxyGivenToDto | null) => {
        setUpcomingMeetings(currentMeetings =>
            currentMeetings.map(meeting => (meeting.id === meetingId ? { ...meeting, myProxyGivenTo } : meeting))
        );
    };

    const handlerOpenMeetingDocuments = async (meeting: HomeUpcomingMeetingItem) => {
        setHomeMeetingFilesLoading(true);
        setSelectedMeeting(meeting);

        try {
            const result = await getMeetingFilesForCurrentUser(meeting.id);
            const files = result
                .filter(doc => Boolean(doc.documentId) && Boolean(doc.fileName))
                .map(doc => ({
                    name: doc.fileName!,
                    data: doc.documentId!,
                    meetingId: meeting.id,
                    meetingDate: meeting.date,
                    meetingStartTime: meeting.startTime,
                    meetingLabel: `${meeting.date} à ${meeting.startTime}`,
                }));

            setHomeMeetingFiles(files);
        } catch (error) {
            console.error('Erreur lors de la récupération des documents de réunion');
            showAxiosMessageError(error as Error, showMessage);
            setSelectedMeeting(undefined);
            setHomeMeetingFiles([]);
        } finally {
            setHomeMeetingFilesLoading(false);
        }
    };

    const handlerCloseMeetingDocuments = () => {
        setSelectedMeeting(undefined);
        setHomeMeetingFiles([]);
        setHomeMeetingFilesLoading(false);
    };

    const handlerLoadHomeMeetingFileContent = async (file: FileDto): Promise<FileDto | undefined> => {
        if (file.content || !file.meetingId) {
            return file;
        }

        try {
            const content = await getMeetingFileContentForCurrentUser(file.meetingId, file.data);
            const updatedFile = { ...file, content };

            setHomeMeetingFiles(currentFiles =>
                currentFiles.map(currentFile =>
                    currentFile.data === updatedFile.data && currentFile.meetingId === updatedFile.meetingId
                        ? updatedFile
                        : currentFile
                )
            );

            return updatedFile;
        } catch (error) {
            console.error('Erreur lors du chargement du document de réunion');
            showAxiosMessageError(error as Error, showMessage);
            return undefined;
        }
    };

    //Conditionnement de la vue
    //Si connecté mmais pas admin : message
    //si connecté admin : list utilisateurs
    //Si pas connecté : login
    if (!auth?.user) {
        return <LoginForm handlerSubmit={handlerSubmit} loading={loading} />;
    }

    return (
        <div className="homePage">
            <Grid container rowSpacing={2} columnSpacing={2}>
                {!loadingUpcomingMeetings && upcomingMeetings.length > 0 && (
                    <Grid item xs={12}>
                        <Paper className="homePage__upcomingPanel" elevation={0}>
                            <div className="homePage__upcomingHeader">
                                <Typography variant="h5">Mes 5 prochaines réunions</Typography>
                                <Typography variant="body2">
                                    Toutes origines confondues, pour accéder plus vite à vos prochains rendez-vous.
                                </Typography>
                            </div>
                            <Stack spacing={1.5}>
                                {upcomingMeetings.map(meeting => (
                                    <Paper key={meeting.id} className="homePage__upcomingMeeting" elevation={0}>
                                        <div className="homePage__upcomingMeetingMain">
                                            <Typography variant="subtitle1">{meeting.title}</Typography>
                                            <Typography variant="body2">
                                                {meeting.date} à {meeting.startTime}
                                            </Typography>
                                        </div>
                                        <Typography variant="body2" className="homePage__upcomingMeetingLocation">
                                            {meeting.location}
                                        </Typography>
                                        <div className="homePage__upcomingMeetingAttendance">
                                            <MeetingAttendance
                                                compact
                                                meeting={meeting}
                                                loading={meetingAttendanceLoadingId === meeting.id}
                                                onChange={attendance =>
                                                    handlerUpdateMeetingAttendance(meeting.id, attendance)
                                                }
                                                onProxyChange={myProxyGivenTo =>
                                                    handlerProxyChange(meeting.id, myProxyGivenTo)
                                                }
                                            />
                                        </div>
                                        {meeting.hasDocument && (
                                            <IconButton
                                                aria-label="Voir les documents"
                                                onClick={() => {
                                                    void handlerOpenMeetingDocuments(meeting);
                                                }}
                                                size="small"
                                                sx={{
                                                    border: '1px solid #d9e3ef',
                                                    borderRadius: '12px',
                                                    color: '#305b8d',
                                                    padding: '8px',
                                                }}
                                            >
                                                <InsertDriveFileOutlinedIcon />
                                            </IconButton>
                                        )}
                                    </Paper>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                )}
                {/*Bloc Assemblée générale*/}
                {user?.mandateAssGeneral && (
                    <Grid item xs={12} sm={6}>
                        <Paper
                            className="homePage__card"
                            onClick={() => {
                                navigate('/elu/ag');
                            }}
                            elevation={0}
                        >
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                <div className="homePage__cardIcon">
                                    <img
                                        src={logoGeneralAssembly}
                                        title={user?.mandateAssGeneral?.title}
                                        alt={user?.mandateAssGeneral?.title}
                                        loading="lazy"
                                    />
                                </div>
                                <div className="homePage__cardContent">
                                    <Typography variant="h6">Assemblée générale</Typography>
                                    <Typography variant="body2" className="homePage__cardSubtitle">
                                        Accès direct aux mandats et données associées.
                                    </Typography>
                                </div>
                            </Stack>
                        </Paper>
                    </Grid>
                )}
                {/*Bloc Bureau Syndical et Conseil d'Administration*/}
                {user?.mandateOfficeAdmin && (
                    <Grid item xs={12} sm={6}>
                        <Paper
                            className="homePage__card"
                            elevation={0}
                            onClick={() => {
                                navigate('/elu/bsca');
                            }}
                        >
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                <div className="homePage__cardIcon">
                                    <img
                                        src={logoOfficeTradeUnion}
                                        title="Bureau syndical et conseil d'administration"
                                        alt="Bureau syndical et conseil d'administration"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="homePage__cardContent">
                                    <Typography variant="h6">Bureau syndical et conseil d'administration</Typography>
                                    <Typography variant="body2" className="homePage__cardSubtitle">
                                        Consultation des informations de gouvernance.
                                    </Typography>
                                </div>
                            </Stack>
                        </Paper>
                    </Grid>
                )}
                {/*Blocs COPE*/}
                {mandatesEstablishment.map((value: MandateEstablishmentDto) => (
                    <Grid item xs={12} sm={6} key={value.id}>
                        <Paper
                            className="homePage__card"
                            elevation={0}
                            onClick={() => {
                                const targetPath = isEauPotableSkillCenter(value.skillCenter)
                                    ? `/elu/eau?e=${value.establishmentId}&m=${value.id}`
                                    : `/elu/assainissement?e=${value.establishmentId}&m=${value.id}`;
                                navigate(targetPath);
                            }}
                        >
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                <div className="homePage__cardIcon">
                                    <img
                                        src={logoCOPE}
                                        title={value.establishmentLabel}
                                        alt={value.establishmentLabel}
                                        loading="lazy"
                                    />
                                </div>
                                <div className="homePage__cardContent">
                                    <Typography variant="h6">COPE : {value.skillCenter}</Typography>
                                    <Typography variant="body2" className="homePage__cardSubtitle">
                                        {value.establishmentLabel}
                                    </Typography>
                                </div>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
                {/*Blocs Territoire*/}
                {mandatesTerritory.map((value: MandateTerritoryDto) => (
                    <Grid item xs={12} sm={6} key={value.id}>
                        <Paper
                            className="homePage__card"
                            elevation={0}
                            onClick={() => {
                                navigate(`/elu/territoire?m=${value.id}`);
                            }}
                        >
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                <div className="homePage__cardIcon">
                                    <img
                                        src={logoTerritory}
                                        title={value.territoryLabel}
                                        alt={value.territoryLabel}
                                        loading="lazy"
                                    />
                                </div>
                                <div className="homePage__cardContent">
                                    <Typography variant="h6">Territoire</Typography>
                                    <Typography variant="body2" className="homePage__cardSubtitle">
                                        {value.territoryLabel}
                                    </Typography>
                                </div>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
                {/*Blocs Bassin*/}
                {mandatesBasin.map((value: MandateGeneralDto) => (
                    <Grid item xs={12} sm={6} key={value.id}>
                        <Paper
                            className="homePage__card"
                            elevation={0}
                            onClick={() => {
                                navigate(`/elu/bassin?m=${value.id}`);
                            }}
                        >
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                <div className="homePage__cardIcon">
                                    <img src={logoTerritory} title={value.title} alt={value.title} loading="lazy" />
                                </div>
                                <div className="homePage__cardContent">
                                    <Typography variant="h6">Bassin</Typography>
                                    <Typography variant="body2" className="homePage__cardSubtitle">
                                        {value.title}
                                    </Typography>
                                </div>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
                {/*Bloc Bureau Syndical et Conseil d'Administration*/}
                {(user?.mandateCommissions || user?.hasCommission) && (
                    <Grid item xs={12} sm={6}>
                        <Paper
                            className="homePage__card"
                            elevation={0}
                            onClick={() => {
                                navigate('/elu/commissions');
                            }}
                        >
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                <div className="homePage__cardIcon">
                                    <img
                                        src={logoGeneralAssembly}
                                        title="Commissions"
                                        alt="Commissions"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="homePage__cardContent">
                                    <Typography variant="h6">Commissions</Typography>
                                    <Typography variant="body2" className="homePage__cardSubtitle">
                                        Accès aux commissions disponibles.
                                    </Typography>
                                </div>
                            </Stack>
                        </Paper>
                    </Grid>
                )}
            </Grid>
            <Dialog
                open={selectedMeeting !== undefined}
                onClose={handlerCloseMeetingDocuments}
                maxWidth="lg"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        maxHeight: '92vh',
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        overflowY: 'hidden',
                        padding: { xs: 2, sm: 3 },
                    }}
                >
                    {selectedMeeting && (
                        <MeetingFilesUi
                            meeting={{
                                id: selectedMeeting.id,
                                date: selectedMeeting.date,
                                startTime: selectedMeeting.startTime,
                                duration: '00h00',
                                location: selectedMeeting.location,
                                pointsReunion: selectedMeeting.pointsReunion,
                                status: selectedMeeting.status,
                                title: selectedMeeting.title,
                                hasDocument: selectedMeeting.hasDocument,
                                attendance: selectedMeeting.attendance,
                                mandateId: selectedMeeting.mandateId,
                                establishmentId: selectedMeeting.establishmentId,
                                territoryId: selectedMeeting.territoryId,
                            }}
                            files={homeMeetingFiles}
                            searching={homeMeetingFilesLoading}
                            handlerLoadFileContent={handlerLoadHomeMeetingFileContent}
                            handlerUpdateMeetingAttendance={async (meeting, attendance) => {
                                await handlerUpdateMeetingAttendance(meeting.id, attendance);
                                const updatedMeeting = {
                                    ...meeting,
                                    attendance,
                                };

                                setSelectedMeeting(currentMeeting =>
                                    currentMeeting && currentMeeting.id === meeting.id
                                        ? { ...currentMeeting, attendance }
                                        : currentMeeting
                                );

                                return updatedMeeting;
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
